import React, { useEffect, useState } from 'react';
import { ActivityIndicator } from 'react-native';

import { ErrorModal } from '~/components/error';
import { Text } from '~/components/text';
import { AuthActionResult, useAuth } from '~/contexts/auth';
import { generateErrorId } from '~/error/app-error-boundary';
import * as ErrorDiagnostics from '~/error/app-error-diagnostics';

import { EIdentVerificationState } from './e-ident-types';

export function EIdentWeb() {
    const { remote } = useAuth();
    const [verificationState, setVerificationState] = useState<EIdentVerificationState>(
        EIdentVerificationState.INITIALISING
    );

    useEffect(() => {
        (async () => {
            try {
                const url = await remote.eidentInit();
                if (url) {
                    setVerificationState(EIdentVerificationState.READY);
                    window.location.href = url;
                } else {
                    setVerificationState(EIdentVerificationState.SERVICE_NOT_AVAILABLE);
                }
            } catch (error) {
                ErrorDiagnostics.error(error);
                setVerificationState(EIdentVerificationState.SERVICE_NOT_AVAILABLE);
            }
        })();
    }, [remote]);

    return (
        <>
            {verificationState === EIdentVerificationState.INITIALISING && <ActivityIndicator />}
            {verificationState === EIdentVerificationState.SERVICE_NOT_AVAILABLE && <Text.H2>Not available</Text.H2>}
        </>
    );
}

type Props = {
    onVerificationCompleted: (result: AuthActionResult) => void;
};

export function EIdentWebVerify(props: Props) {
    const { onVerificationCompleted } = props;
    const { remote } = useAuth();

    useEffect(() => {
        const handleVerification = async () => {
            try {
                const url = window.location.href;
                const urlParams = url.slice(url.indexOf('?') + 1);

                const result = await remote.eidentVerify(urlParams);
                onVerificationCompleted(result);
                // Note: Don't call window.location.replace('/') here.
                // The auth state change from result.commit() will trigger the app to re-render
                // with the authenticated navigator, which handles navigation automatically.
                // A full page reload causes race conditions with the linking config that can
                // result in redirect loops and SecurityError with accumulated slashes in URLs.
            } catch (error) {
                ErrorDiagnostics.error(error);
                onVerificationCompleted({ success: false });
            }
        };
        handleVerification();
    }, [onVerificationCompleted, remote]);

    return (
        <>
            <ActivityIndicator />
            <Text.P1>E-ident verification in progress!</Text.P1>
        </>
    );
}

type EIdentWebErrorProps = {
    onRetry: () => void;
};

export const EIdentWebError: React.FC<EIdentWebErrorProps> = ({ onRetry }) => {
    const authError = new Error('E-ident web authentication failed');
    const errorId = generateErrorId();
    ErrorDiagnostics.error(authError, { errorUuid: errorId, component: 'e-ident-web' });
    return <ErrorModal id={errorId} errorType="auth" onRetry={onRetry} />;
};
